import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { SVMChain } from '../../types';
import { isEVMChain } from '../../wallet/chains';
import { AVAILABLE_EVM_CHAINS } from '../../wallet';
import { DEFAULT_DAPP_STATE } from '../constants';
import { getChainState, patchChainState, removeInvalidNFTs } from '../utils/chain-state';
import { fetchEvmListedNfts, fetchEvmMyNfts, isEvmChainReady, updateEVMGlobalState, updateEVMUserState, } from '../evm/evm-chain-data';
import { fetchSvmListedNfts, fetchSvmMyNfts, isSvmChainReady, updateSVMGlobalState, updateSVMUserState, } from '../svm/svm-chain-data';
export function useChainData({ state, setState, setBalances, toast, isWalletAuthenticated = false, }) {
    const svmWallet = useWallet();
    const wasAuthenticatedRef = useRef(isWalletAuthenticated);
    // Drop personal wallet data when sign-in ends (disconnect / auth failure).
    useEffect(() => {
        const wasAuthenticated = wasAuthenticatedRef.current;
        wasAuthenticatedRef.current = isWalletAuthenticated;
        if (isWalletAuthenticated || !wasAuthenticated)
            return;
        const personalReset = {
            myNfts: [],
            myListedNfts: [],
            walletAddress: '',
            weiProceeds: undefined,
            proceeds: '0',
            isApprovedMarket: [],
            isLoadingUserState: false,
            isLoadingMyNfts: false,
        };
        setState((prev) => {
            let next = prev;
            for (const chain of Object.keys(prev)) {
                next = patchChainState(next, chain, personalReset);
            }
            return next;
        });
    }, [isWalletAuthenticated, setState]);
    const evmDeps = useMemo(() => ({
        setState,
        setBalances,
    }), [setState, setBalances]);
    const svmDeps = useMemo(() => ({
        setState,
        setBalances,
        svmWallet,
        toast,
    }), [setState, setBalances, svmWallet, toast]);
    const updateGlobalStateByChain = useCallback(async (chain) => {
        if (!getChainState(state, chain).isLoadingGlobalState) {
            setState((prev) => patchChainState(prev, chain, { isLoadingGlobalState: true }));
            if (chain === SVMChain.Solana) {
                await updateSVMGlobalState(chain, svmDeps, getChainState, patchChainState);
            }
            else {
                await updateEVMGlobalState(chain, evmDeps, getChainState, patchChainState);
            }
            setState((prev) => patchChainState(prev, chain, { isLoadingGlobalState: false }));
        }
    }, [state, setState, evmDeps, svmDeps]);
    const updateMyStateByChain = useCallback(async (chain) => {
        if (!getChainState(state, chain).isLoadingUserState) {
            setState((prev) => patchChainState(prev, chain, { isLoadingUserState: true }));
            if (chain === SVMChain.Solana) {
                await updateSVMUserState(chain, svmDeps, getChainState, patchChainState);
            }
            else {
                await updateEVMUserState(chain, evmDeps, getChainState, patchChainState);
            }
            setState((prev) => patchChainState(prev, chain, {
                listedNfts: getChainState(prev, chain).allListedNfts ?? [],
                isLoadingUserState: false,
            }));
        }
    }, [state, setState, evmDeps, svmDeps]);
    const loadAllListedNftsByChain = useCallback(async (chain) => {
        if (!getChainState(state, chain).isLoadingListedNfts) {
            setState((prev) => patchChainState(prev, chain, { isLoadingListedNfts: true }));
            let listedNfts = [];
            try {
                const chainState = getChainState(state, chain);
                listedNfts =
                    chain === SVMChain.Solana
                        ? await fetchSvmListedNfts(chainState.allListedNfts)
                        : await fetchEvmListedNfts(chain, chainState.allListedNfts);
            }
            catch {
                toast.error(`Unable to load NFTs. [Retry]`);
            }
            listedNfts = removeInvalidNFTs(listedNfts);
            setState((prev) => {
                const chainState = getChainState(prev, chain);
                return patchChainState(prev, chain, {
                    allListedNfts: listedNfts,
                    myListedNfts: listedNfts.filter((nft) => nft.owner == chainState.walletAddress),
                    listedNfts,
                    isLoadingListedNfts: false,
                });
            });
        }
    }, [state, setState, toast]);
    const loadMyNftsByChain = useCallback(async (chain) => {
        if (!getChainState(state, chain).isLoadingMyNfts) {
            setState((prev) => patchChainState(prev, chain, { isLoadingMyNfts: true }));
            let myNfts = [];
            try {
                const chainState = getChainState(state, chain);
                myNfts =
                    chain === SVMChain.Solana
                        ? await fetchSvmMyNfts(chainState.myNfts)
                        : await fetchEvmMyNfts(chain, chainState.myNfts);
            }
            catch {
                toast.error(`Unable to load NFTs. [Retry]`);
            }
            myNfts = removeInvalidNFTs(myNfts);
            setState((prev) => patchChainState(prev, chain, { myNfts, isLoadingMyNfts: false }));
        }
    }, [state, setState, toast]);
    const updateGlobalDataByChain = useCallback(async (chain) => {
        await updateGlobalStateByChain(chain);
        await loadAllListedNftsByChain(chain);
    }, [updateGlobalStateByChain, loadAllListedNftsByChain]);
    const updateMyDataByChain = useCallback(async (chain) => {
        await updateMyStateByChain(chain);
        await loadMyNftsByChain(chain);
    }, [updateMyStateByChain, loadMyNftsByChain]);
    const markChainInactive = useCallback((chain) => {
        setState((prev) => patchChainState(prev, chain, {
            isActive: false,
            isLoadingGlobalState: false,
            isLoadingUserState: false,
            isLoadingListedNfts: false,
            isLoadingMyNfts: false,
        }));
    }, [setState]);
    const isChainContractsReady = useCallback((chain) => {
        if (isEVMChain(chain))
            return isEvmChainReady(chain);
        if (chain === SVMChain.Solana)
            return isSvmChainReady();
        return true;
    }, []);
    const updateChainState = useCallback((chain, shouldUpdateOnlyUser, shouldShowToast) => {
        if (!isChainContractsReady(chain)) {
            markChainInactive(chain);
            return;
        }
        if (shouldShowToast)
            toast.info(`Loading ${chain} State...`, { autoClose: 1000 });
        if (!shouldUpdateOnlyUser)
            updateGlobalDataByChain(chain);
        // /owned, proceeds, approvals, my NFTs — only after wallet challenge succeeds.
        if (isWalletAuthenticated)
            updateMyDataByChain(chain);
    }, [
        isChainContractsReady,
        markChainInactive,
        toast,
        updateGlobalDataByChain,
        updateMyDataByChain,
        isWalletAuthenticated,
    ]);
    const updateAllSVMChains = useCallback((shouldUpdateOnlyUser) => {
        updateChainState(SVMChain.Solana, shouldUpdateOnlyUser, true);
    }, [updateChainState]);
    const updateAllEVMChains = useCallback((shouldUpdateOnlyUser) => {
        toast.info(`Loading EVM State...`, { autoClose: 1000 });
        AVAILABLE_EVM_CHAINS.forEach((chain) => {
            if (DEFAULT_DAPP_STATE[chain]) {
                updateChainState(chain, shouldUpdateOnlyUser, false);
            }
        });
    }, [toast, updateChainState]);
    const refreshSvmGlobalState = useCallback(async () => {
        await updateSVMGlobalState(SVMChain.Solana, svmDeps, getChainState, patchChainState);
    }, [svmDeps]);
    const refreshEvmGlobalState = useCallback(async (chain) => {
        await updateEVMGlobalState(chain, evmDeps, getChainState, patchChainState);
    }, [evmDeps]);
    return {
        updateChainState,
        updateAllSVMChains,
        updateAllEVMChains,
        refreshSvmGlobalState,
        refreshEvmGlobalState,
    };
}
